import {
  AccountingDocumentListItem,
  AccountingDocumentStatus,
} from '@interfaces/accounting-data-standardization/responses/accounting-document-list-item.response';
import { DocumentAnalysisSituationKind } from '@interfaces/accounting-data-standardization/responses/document-analysis-situation.response';

/**
 * Leitura de "documentos contábeis em processamento" para o aviso da Análise Econômica
 * (`app-dfp-processing-documents`). Funções puras: o componente só busca a lista e agenda o polling.
 *
 * ## Por que o aviso existe
 *
 * Reprocessar um documento contábil tira a validação dele, e documento sem validação sai da disputa de "qual
 * documento vale em cada ano" até ser validado de novo. Nesse intervalo a tela mostra OUTRO documento naquele
 * ano (caso VITALUX-ECOATIVA: 2026 passou a vir do "2026-03 DRE" enquanto os dois "2026-08 Balanço Patrimonial"
 * eram reextraídos), e quem olha não sabe que é temporário.
 */

/** Etapa de um documento, em linguagem de negócio. */
export type DfpProcessingStage = 'reading' | 'mapping' | 'awaitingValidation' | 'error';

/** Um documento em andamento, já traduzido para o aviso. */
export interface DfpProcessingDocument {
  item: AccountingDocumentListItem;
  stage: DfpProcessingStage;
}

/**
 * Status pedidos ao backend. `Invalidado` entra porque é o estado de quem foi reavaliado (validação desfeita) e
 * espera validar de novo: para a tela, é o mesmo "aguardando validação" de um `Extraido`. `NaoExtraido` fica de
 * fora: é o legado pré-Fase 2, parado, e não vai mudar sozinho.
 */
export const DFP_PROCESSING_STATUSES: AccountingDocumentStatus[] = [
  'Processando',
  'Extraido',
  'Invalidado',
  'Erro',
];

/**
 * Situações da Análise Econômica (card 3700, decididas no backend) que fazem de um documento não validado só um
 * HISTÓRICO: o período dele já vale por outro documento validado (`PeriodCovered`), ele foi substituído
 * (`Superseded`) ou perdeu o arquivo de origem (`SourceFileMissing`). Ninguém precisa validar esses documentos, e
 * contá-los no aviso fazia a pessoa achar que havia algo para aprovar (card 3708, 26/10/2026).
 */
const HISTORICAL_SITUATION_KINDS: ReadonlySet<DocumentAnalysisSituationKind> = new Set([
  'PeriodCovered',
  'Superseded',
  'SourceFileMissing',
]);

/** Frase de reserva do histórico quando o backend não mandou a `message` da situação. */
export const DFP_HISTORICAL_FALLBACK_MESSAGE =
  'O período deste documento já vale por outro documento validado. Este fica só como histórico.';

/** Documentos em andamento separados entre o que pede ação de alguém e o que é só histórico. */
export interface DfpProcessingGroups {
  /** Lendo, no De/Para, com erro ou aguardando validação de verdade (`Pending`, `OnlyDocumentForYear` ou sem situação). */
  actionable: DfpProcessingDocument[];
  /** Aguardando validação, mas com período já coberto, substituído ou sem arquivo de origem: não pede ação. */
  historical: DfpProcessingDocument[];
}

/**
 * O documento é só histórico? Só vale para quem aguarda validação (`Extraido`/`Invalidado` com De/Para concluído):
 * documento sendo lido, no De/Para ou com erro continua pedindo atenção como antes, qualquer que seja a situação.
 */
export function isHistoricalDocument(doc: DfpProcessingDocument): boolean {
  const kind = doc.item.analysisSituation?.kind;
  return doc.stage === 'awaitingValidation' && !!kind && HISTORICAL_SITUATION_KINDS.has(kind);
}

/** Separa os documentos em "pede ação" e "histórico", mantendo a ordem de `toProcessingDocuments`. */
export function splitProcessingDocuments(documents: DfpProcessingDocument[]): DfpProcessingGroups {
  const actionable: DfpProcessingDocument[] = [];
  const historical: DfpProcessingDocument[] = [];
  for (const doc of documents) {
    (isHistoricalDocument(doc) ? historical : actionable).push(doc);
  }
  return { actionable, historical };
}

/** Frase do item histórico no popover: a `message` pronta do backend, ou a de reserva. */
export function historicalMessage(doc: DfpProcessingDocument): string {
  return doc.item.analysisSituation?.message?.trim() || DFP_HISTORICAL_FALLBACK_MESSAGE;
}

/** Rótulo de cada etapa no popover. */
export const DFP_PROCESSING_STAGE_LABEL: Record<DfpProcessingStage, string> = {
  reading: 'Lendo o documento',
  mapping: 'Fazendo o De/Para',
  awaitingValidation: 'Aguardando validação',
  error: 'Erro',
};

/** Ordem do popover: o que anda sozinho primeiro, erro por último. */
const STAGE_ORDER: Record<DfpProcessingStage, number> = {
  reading: 0,
  mapping: 1,
  awaitingValidation: 2,
  error: 3,
};

/** Intervalo do polling enquanto há leitura ou De/Para rodando (a máquina está trabalhando). */
export const DFP_PROCESSING_FAST_POLL_MS = 10_000;

/** Intervalo do polling quando só há documento esperando validação (depende de uma pessoa). */
export const DFP_PROCESSING_SLOW_POLL_MS = 30_000;

/**
 * Etapa de um item da lista da Padronização, ou `null` quando ele não está em andamento.
 *
 * Só Estrutura Contábil: Faturamento não disputa coluna de DRE/Balanço. O De/Para vem de `deParaState`, campo
 * próprio, porque o status do documento já diz "Extraído" enquanto a etapa de De/Para ainda está na fila
 * (README da Padronização, "Etapa automática de De/Para").
 */
export function toProcessingStage(item: AccountingDocumentListItem): DfpProcessingStage | null {
  if (item.type !== 'EstruturaContabil') return null;

  switch (item.status) {
    case 'Processando':
      return 'reading';
    case 'Erro':
      return 'error';
    case 'Extraido':
    case 'Invalidado':
      return item.deParaState === 'Enqueued' ? 'mapping' : 'awaitingValidation';
    default:
      return null;
  }
}

/**
 * Itens em andamento, ordenados por etapa e, dentro dela, pelo mais recente.
 *
 * Todo documento que aguarda validação ou parou com erro conta, por mais antigo que seja (decisão de 19/10/2026).
 * Antes havia uma janela de 7 dias para não poluir a faixa com pendência antiga, mas ela escondia justamente o
 * documento que era a única fonte da DRE da empresa: a tela ficava sem DRE e sem nenhum aviso do porquê.
 */
export function toProcessingDocuments(
  items: AccountingDocumentListItem[] | null | undefined
): DfpProcessingDocument[] {
  return (items ?? [])
    .map(item => ({ item, stage: toProcessingStage(item) }))
    .filter((entry): entry is DfpProcessingDocument => entry.stage !== null)
    .sort(
      (a, b) =>
        STAGE_ORDER[a.stage] - STAGE_ORDER[b.stage] ||
        (b.item.processedAt ?? '').localeCompare(a.item.processedAt ?? '')
    );
}

/**
 * Próxima consulta, em ms, ou `null` para parar.
 *
 * - Leitura ou De/Para rodando: rápido, a mudança vem sozinha em minutos.
 * - Só "aguardando validação": devagar, depende de alguém validar (mas é exatamente essa validação que a tela
 *   precisa perceber para recarregar).
 * - Só erro, ou nada: para. Erro é fim de linha até alguém reprocessar.
 *
 * O componente passa só os documentos que pedem ação: histórico não muda sozinho nem espera ninguém.
 */
export function nextProcessingPollDelay(documents: DfpProcessingDocument[]): number | null {
  if (documents.some(doc => doc.stage === 'reading' || doc.stage === 'mapping')) {
    return DFP_PROCESSING_FAST_POLL_MS;
  }
  if (documents.some(doc => doc.stage === 'awaitingValidation')) {
    return DFP_PROCESSING_SLOW_POLL_MS;
  }
  return null;
}

/**
 * O conjunto de documentos fora da disputa mudou entre duas consultas? Saiu algum (validou, ou foi removido) ou
 * entrou algum (foi reavaliado, então a coluna dele já mudou na tela). Em qualquer dos casos a DFP precisa
 * recarregar. Documento que só trocou de etapa (lendo para aguardando validação) não muda nada na tela.
 */
export function hasProcessingSetChanged(previousIds: string[], currentIds: string[]): boolean {
  if (previousIds.length !== currentIds.length) return true;
  const previous = new Set(previousIds);
  return currentIds.some(id => !previous.has(id));
}

/**
 * Frase do aviso: o que está acontecendo, o que esperar e onde agir. Sem travessão e sem jargão de sistema
 * ("processamento" ficou de fora no gate de pré-push: é palavra do sistema, não de quem lê a tela).
 *
 * Exemplos: "2 documentos contábeis estão sendo lidos ou aguardam validação: os valores deles entram nesta tela
 * quando forem validados. Veja quais em Ver documentos." / "1 documento contábil parou com erro: os valores dele
 * só entram nesta tela depois de reprocessado e validado na Padronização. Veja qual em Ver documentos."
 *
 * Recebe só os documentos que pedem ação (`splitProcessingDocuments(...).actionable`): o histórico não entra na
 * contagem nem na frase (card 3708).
 */
export function buildProcessingSummary(documents: DfpProcessingDocument[]): string {
  const pending = documents.filter(doc => doc.stage !== 'error').length;
  const failed = documents.length - pending;
  if (!documents.length) return '';

  const parts: string[] = [];

  if (pending > 0) {
    parts.push(
      pending === 1
        ? '1 documento contábil está sendo lido ou aguarda validação: os valores dele entram nesta tela quando for validado.'
        : `${pending} documentos contábeis estão sendo lidos ou aguardam validação: os valores deles entram nesta tela quando forem validados.`
    );
  }

  if (failed > 0) {
    const subject =
      pending > 0 ? '' : failed === 1 ? ' documento contábil' : ' documentos contábeis';
    parts.push(
      failed === 1
        ? `1${subject} parou com erro: os valores dele só entram nesta tela depois de reprocessado e validado na Padronização.`
        : `${failed}${subject} pararam com erro: os valores deles só entram nesta tela depois de reprocessados e validados na Padronização.`
    );
  }

  parts.push(
    documents.length === 1 ? 'Veja qual em Ver documentos.' : 'Veja quais em Ver documentos.'
  );
  return parts.join(' ');
}
